import { ChangeDetectionStrategy, Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { AppService } from '../../app.service';
import { RBACService } from '../../shared/rbac.service';
import { UtilsService } from '../../shared/utils.service';
import { AccessRequestsService } from '../access-requests/access-requests.service';
import { AccessRequestsComponent } from '../access-requests/access-requests.component';

/**
 * The Employees area shell (SC-301).
 *
 * Employees previously routed straight to EmployeesComponent with no shell. Access Requests is deliberately
 * a tab here rather than a separate menu entry: deciding who may join the account is part of managing the
 * account's people, so keeping the two together is where a reviewer will look for it.
 *
 * The two tabs are gated independently, because Employees: Access Requests is a distinct permission from
 * Employees. An account can hand somebody the approvals queue without handing them the employee register.
 */
@Component({
  selector: 'app-employees-layout',
  templateUrl: './employees-layout.component.html',
  styleUrls: ['./employees-layout.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class EmployeesLayoutComponent implements OnInit, OnDestroy {
  // Resolved ONCE in ngOnInit() so the template does not re-run permission scans on every change
  // detection cycle.
  canViewEmployeesTab: boolean = false;
  canViewRequestsTab: boolean = false;

  selectedTabIndex: number = 0;

  // The pending count drives the tab badge. Held here rather than in the list component so the badge is
  // correct before the user ever opens the tab.
  pendingCount: number = 0;

  private pendingCountSubscription?: Subscription;

  @ViewChild(AccessRequestsComponent) accessRequestsComponent: AccessRequestsComponent;

  constructor(
    public app: AppService,
    public utils: UtilsService,
    public rbac: RBACService,
    private accessRequestsService: AccessRequestsService,
    private router: Router
  ) {}

  ngOnInit(): void {
    // A menu entry shows on ANY employees permission, so the tab must match: a create-only user still
    // needs the tab that holds the create button.
    this.canViewEmployeesTab = this.rbac.userCanAccess([
      RBACService.getPermission(RBACService.RBAC_F.F_EMPLOYEES, RBACService.RBAC_A.A_VIEW),
      RBACService.getPermission(RBACService.RBAC_F.F_EMPLOYEES, RBACService.RBAC_A.A_CREATE),
      RBACService.getPermission(RBACService.RBAC_F.F_EMPLOYEES, RBACService.RBAC_A.A_EDIT),
      RBACService.getPermission(RBACService.RBAC_F.F_EMPLOYEES, RBACService.RBAC_A.A_DELETE)
    ]);

    this.canViewRequestsTab = this.rbac.userCanAccess([
      RBACService.getPermission(RBACService.RBAC_F.F_EMPLOYEES_ACCESS_REQUESTS, RBACService.RBAC_A.A_VIEW)
    ], true);

    // Neither tab means there is nothing on this page to show.
    if ( !this.canViewEmployeesTab && !this.canViewRequestsTab ) {
      this.rbac.showGenericAccessDeniedMessage('You do not have permission to access this feature.');
      this.router.navigate(['/dashboard']);
      return;
    }

    // Land on the first PERMITTED tab, not index 0. A user holding only the approvals permission would
    // otherwise open onto a hidden Employees tab and see an empty page, which reads as a broken
    // permission rather than a deliberate separation.
    this.selectedTabIndex = this.canViewEmployeesTab ? 0 : 1;

    if ( this.canViewRequestsTab ) {
      // The service keeps one count that both this badge and the left navigation read, so they cannot
      // disagree.
      this.pendingCountSubscription = this.accessRequestsService.pendingCount.subscribe(
        (count: number): void => {
          this.pendingCount = count;
        }
      );
      this.accessRequestsService.refreshPendingCount();
    }
  }

  ngOnDestroy(): void {
    this.pendingCountSubscription?.unsubscribe();
  }

  /**
   * Loads the Access Requests list the first time its tab is opened.
   *
   * The list lives inside a lazy matTabContent template, so it does not exist until the tab is shown.
   * Guarded on the permission because a parent calling getData() directly would otherwise bypass the
   * child's own ngOnInit permission check, which is the trap called out in conventions.md.
   */
  onTabChanged(): void {
    if ( !this.canViewRequestsTab ) {
      return;
    }
    this.accessRequestsService.refreshPendingCount();
  }

  protected readonly RBACService = RBACService;
  protected readonly AppService = AppService;
}
